import { useEffect, useState, useCallback, useRef } from 'react';
import { useMediaQuery, Button, styled } from '@mui/material';
import config from '../config.json';
import {
  StyledTablePagination,
  useSort,
  variables,
  useCancelRequests,
  useAlertContext,
} from '../common';
import {
  useRequestSearch,
  RequestStatus2d,
  RequestStatusStack,
  properties,
  RequestStatusFilterSortStack,
  RequstStatusFilter2d,
} from '.';
import { pathOr, always } from 'rambda';
import { noop } from 'lodash/fp';
import { flow } from 'fp-ts/function';

const rowsPerPageOptions = [10, 25, 50, 100, 500];

export const RequestStatus = ({
  di,
  requestorEmail,
  disableOperationsHelpDsk,
  legacy,
}: {
  di: boolean;
  requestorEmail: string;
  disableOperationsHelpDsk: boolean;
  legacy?: boolean;
}) => {
  const [searchBy, setSearchBy] = useState('reqType');
  const [searchP1, setSearchP1] = useState('');
  const [searchP2, setSearchP2] = useState('');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = flow(
    pathOr('10', 'target.value'),
    (a: string) => parseInt(a, 10),
    setPageSize,
    always(0),
    setPage
  );

  const { sortBy, sortDirection, toggleSort } = useSort('REQUEST_ID', 'asc');

  const { requestStatus, call, search: _search } = useRequestSearch({
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchBy,
    searchP1: searchP1.trim(),
    searchP2,
    di,
    requestorEmail,
    legacy,
  });

  const searchRef = useRef(_search);
  useEffect(() => { searchRef.current = _search; });

  const hasSearched = useRef(true);
  const isResettingPage = useRef(false);

  const search = useCallback(() => {
    hasSearched.current = true;
    isResettingPage.current = true;
    setPage(0);
    searchRef.current();
  }, []);

  useEffect(() => {
    if (!hasSearched.current) return;
    if (isResettingPage.current) {
      isResettingPage.current = false;
      return;
    }
    searchRef.current();
   },  [sortBy, sortDirection, page, pageSize]);
  // useEffect(_search, [sortBy, sortDirection, page, pageSize, _search]);

  const [selectedRequestIds, setSelectedRequestIds] = useState<number[]>([]);

  const toggleRequestSelection = (payload: any) => {
    const index = selectedRequestIds.indexOf(payload.requestId);
    if (index > -1) {
      let updatedIds = selectedRequestIds.filter(
        requestId => requestId !== payload.requestId
      );
      setSelectedRequestIds(updatedIds);
    } else {
      setSelectedRequestIds([...selectedRequestIds, payload.requestId]);
    }
  };

  const { cancelRequestsStatus, cancelRequests, resetCancelRequests } =
    useCancelRequests({
      requestIds: selectedRequestIds,
      admin: true
    });

  const { setAlert } = useAlertContext();

  const confirmCancelRequests = () => {
    setAlert(a => ({
      ...a,
      title: 'CSOS',
      message: () => <>{config.requestCancelConfirm}</>,
      okAction: true,
      cancelAction: true,
      isOpen: true,
      isOk: false,
      onOk: cancelRequests,
    }));
  };

  useEffect(() => {
    const clearAlert = () =>
      setAlert(a => ({
        ...a,
        isOk: false,
        isOpen: false,
      }));

    const clearRequestSelection = () => setSelectedRequestIds([]);

    cancelRequestsStatus === null &&
      setAlert(a => ({
        ...a,
        title: 'CSOS Canceling Requests',
        message: () => <>{config.requestCanceling}</>,
        okAction: false,
        cancelAction: false,
        isOpen: true,
        onOk: noop,
      }));

    cancelRequestsStatus === false &&
      setAlert(a => ({
        ...a,
        title: 'CSOS Failure',
        message: () => <>{config.requestCancelFailure}</>,
        okAction: true,
        cancelAction: false,
        isOpen: true,
        onOk: flow(clearAlert, resetCancelRequests),
      }));

    cancelRequestsStatus === true &&
      flow(
        () => {
          const newAlert = {
            title: 'CSOS Success',
            message: () => <>{config.requestCancelSuccess}</>,
            okAction: true,
            cancelAction: false,
            isOpen: true,
            onOk: flow(clearAlert, resetCancelRequests),
          };
          setAlert(a => ({
            ...a,
            ...newAlert,
          }));
        },
        search,
        clearRequestSelection
      )();
  }, [cancelRequestsStatus, search, setAlert, resetCancelRequests]);

  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const toggleDrawer = (open: boolean) => (event: any) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setOpenDrawer(open);
  };

  return (
    <Article>
      {matchesGridBreakpoint && (
        <>
          <RequstStatusFilter2d
            di={di}
            searchBy={searchBy}
            searchP1={searchP1}
            searchP2={searchP2}
            setSearchBy={setSearchBy}
            setSearchP1={setSearchP1}
            setSearchP2={setSearchP2}
            search={search}
            legacy={legacy}
          />
          {requestStatus.totalElements > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={requestStatus.totalElements}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          )}
        </>
      )}
      {!matchesGridBreakpoint && (
        <article
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <StyledTablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            count={requestStatus.totalElements}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <Button onClick={toggleDrawer(true)} variant="contained">
            search / sort
          </Button>
        </article>
      )}
      {matchesGridBreakpoint ? (
        <RequestStatus2d
          properties={properties}
          requests={requestStatus.content}
          getRequstsCallStatus={call}
          sortBy={sortBy}
          sortDirection={sortDirection}
          toggleSort={toggleSort}
          selectedRequestIds={selectedRequestIds}
          disableOperationsHelpDsk={disableOperationsHelpDsk}
          toggleRequestSelection={toggleRequestSelection}
          confirmCancelRequests={confirmCancelRequests}
        />
      ) : (
        <RequestStatusStack
          properties={properties}
          requests={requestStatus.content}
          getRequstsCallStatus={call}
          selectedRequestIds={selectedRequestIds}
          disableOperationsHelpDsk={disableOperationsHelpDsk}
          toggleRequestSelection={toggleRequestSelection}
          confirmCancelRequests={confirmCancelRequests}
        />
      )}
      {requestStatus.totalElements > pageSize && (
        <StyledTablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          count={requestStatus.totalElements}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <RequestStatusFilterSortStack
        di={di}
        properties={properties}
        sortBy={sortBy}
        sortDirection={sortDirection}
        openDrawer={openDrawer}
        searchBy={searchBy}
        searchP1={searchP1}
        searchP2={searchP2}
        toggleSort={toggleSort}
        toggleDrawer={toggleDrawer}
        search={search}
        setSearchBy={setSearchBy}
        setSearchP1={setSearchP1}
        setSearchP2={setSearchP2}
      />
      {/* <pre>
        cancelRequestsStatus: {JSON.stringify(cancelRequestsStatus, null, 2)}
      </pre> */}
    </Article>
  );
};

const Article = styled('article')(({ theme }) => ({
  ...variables.childrenMarginTop2x,
}));
